import './DataRetentionPolicies.scss'
import React, { useState } from 'react'
import { Col, Container, Dropdown, Row } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetRetentionPoliciesQuery } from '../../../store/account/account'
import { TableColumn } from 'react-data-table-component'
import { ActionButton } from '../../../components/DataTable/ActionButton'
import {
  EventRetentionRule,
  FileRetention,
  RetentionPolicy,
  RetentionResourceType,
  RetentionRuleType,
  SessionRetention,
  TTLRetentionRule,
} from '../../../models/RetentionPolicy'
import { RetentionPolicyTTLConfigView } from './RetentionPolicyTTLRuleBody'
import { CustomDropdownItem } from '../../../components/Dropdown/CustomDropdownItem'

import { CreateOrEditPolicyModal } from './CreateOrEditPolicyModal'
import { DeletePolicyModal } from './DeletePolicyModal'
import { ViewPolicyModal } from './ViewPolicyModal'
import { RetentionPolicyRuleType } from './RetentionPolicyRuleType'
import { RetentionPolicyResourceType } from './RetentionPolicyResourceType'
import { PIIRedactionConfig } from './PIIRedactionConfig'

const _ALL_RESOURCE_MESSAGE = 'ALL'

const fileKinds: { [key: string]: string } = {
  attachment: 'Attachment',
  recording: 'Meeting',
  transcription: 'Transcription',
}

const getFileKindLabel = (kind?: string | null) => {
  return fileKinds[kind ?? ''] || kind
}

export const DataRetentionPolicies: React.FC = () => {
  return (
    <Container fluid className='p-0'>
      <Row className='mb-3'>
        <Col>
          <div className='lead-medium'>Defaults</div>
        </Col>
      </Row>
      <Row className='zocks-mb-2.5'>
        <Col>
          <DefaultRetentionPolicies />
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col>
          <div className='lead-medium'>Custom Policies</div>
        </Col>
      </Row>
      <Row>
        <Col>
          <CustomRetentionPolicies />
        </Col>
      </Row>
      <Row>
        <Col>
          <PIIRedactionConfig />
        </Col>
      </Row>
    </Container>
  )
}

export const DefaultRetentionPolicies: React.FC = () => {
  const [showEditPolicyModal, setShowEditPolicyModal] = useState<boolean>(false)
  const accountId = useAppSelector(selectAccountId)
  const { data } = useGetRetentionPoliciesQuery({ accountId: accountId })
  const defaultFileDataPolicy = data?.data.find((data) => data.isDefault && data.resource.type === RetentionResourceType.FILE)?.rule as TTLRetentionRule
  const defaultSessionDataPolicy = data?.data.find((data) => data.isDefault && data.resource.type === RetentionResourceType.SESSION)?.rule as TTLRetentionRule
  const configuredAtTheEnterpriseLevel = data?.data.some((data) => data.enterprise) || false
  const handleEditPolicyModalHide = () => {
    setShowEditPolicyModal(false)
  }

  const handleAddCustomPolicy = () => {
    setShowEditPolicyModal(true)
  }
  return (
    <Container className='data-retention-defaults-container zocks-py-1.25 px-4'>
      <CreateOrEditPolicyModal show={showEditPolicyModal} onHide={handleEditPolicyModalHide} />
      {configuredAtTheEnterpriseLevel ? (
        <Row className='p-3 mb-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <div className='d-flex align-self-center gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span className='zocks-text-gray-900'>Retention policies are managed at the enterprise level.</span>
            </div>
          </Col>
        </Row>
      ) : null}
      <Row className='gy-3'>
        <Col className='p-0'>
          <Container>
            <Row className='zocks-mb-1.25'>
              <Col>
                <h4>Retention Policies</h4>
              </Col>
            </Row>
            <Row className='zocks-mb-4'>
              <Col>
                <div className='paragraph-small'>
                  Data retention policies define how long we retain your data. You can override these policies by clicking on "Add Custom Policy" if needed. By
                  default, we retain all your data for 2 years.
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <ScopedBlock
                  quota={{
                    quota: 'retention.enabled.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant='primary' onClick={handleAddCustomPolicy} disabled={configuredAtTheEnterpriseLevel}>
                    <i className='bi bi-plus' />
                    Add custom policy
                  </Button>
                </ScopedBlock>
              </Col>
            </Row>
          </Container>
        </Col>
        <Col md='auto' className='p-0'>
          <div className='rectangle-circle'>
            <div className='mid-top default-type-retention d-flex gap-1 align-items-center'>
              <i className='bi bi-hourglass-split' />
              <div className='paragraph-small'>
                {defaultFileDataPolicy ? <RetentionPolicyTTLConfigView ttlConfig={defaultFileDataPolicy.config} /> : '720days'}
              </div>
            </div>
            <div className='mid-bottom default-type-retention d-flex gap-1 align-items-center'>
              <i className='bi bi-hourglass-split' />
              <div className='paragraph-small'>
                {defaultSessionDataPolicy ? <RetentionPolicyTTLConfigView ttlConfig={defaultSessionDataPolicy.config} /> : '720days'}
              </div>
            </div>

            <div className='left-mid d-flex flex-column zocks-gap-1.25 py-2 default-types'>
              <div className='d-flex gap-1 align-items-center'>
                <b>
                  <i className='default-type-icon-wrapper p-1 p-md-2 bi bi-volume-up' />
                </b>

                <div className='paragraph-medium'>
                  <b>Meeting</b>
                </div>
              </div>
              <div className='d-flex gap-1 align-items-center'>
                <i className='default-type-icon-wrapper p-1 p-md-2 bi bi-link-45deg' />
                <b>Result</b>
              </div>
              <div className='d-flex gap-1 align-items-center'>
                <i className='default-type-icon-wrapper p-1 p-md-2 bi bi-file-text' />
                <b>Transcription</b>
              </div>
            </div>

            <div className='right-mid d-flex align-items-center'>
              <div className='default-type-delete'>
                <i className='bi bi-trash zocks-text-primary' />
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  )
}

const CustomRetentionPolicies = () => {
  const { policyId = null, modal = null } = useParams()
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const [selectedRetentionPolicyId, setSelectedRetentionPolicyId] = useState<string | null>(policyId)
  const [showEditPolicyModal, setShowEditPolicyModal] = useState<boolean>(modal === 'edit')
  const [showViewPolicyModal, setShowViewPolicyModal] = useState<boolean>(modal === 'view')
  const [showDeletePolicyModal, setShowDeletePolicyModal] = useState<boolean>(false)
  const { data, isFetching } = useGetRetentionPoliciesQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const configuredAtTheEnterpriseLevel = data?.data.some((data) => data.enterprise) || false

  const handleOnClickEditPolicy = (policy: RetentionPolicy) => {
    setSelectedRetentionPolicyId(policy.id)
    setShowEditPolicyModal(true)
    navigate(`/workspace/setup/data-retention/policies/${policy.id}/edit`, { replace: true })
  }

  const handleOnClickViewPolicy = (policy: RetentionPolicy) => {
    setSelectedRetentionPolicyId(policy.id)
    setShowViewPolicyModal(true)
    navigate(`/workspace/setup/data-retention/policies/${policy.id}/view`, { replace: true })
  }

  const handleOnClickDeletePolicy = (policy: RetentionPolicy) => {
    setSelectedRetentionPolicyId(policy.id)
    setShowDeletePolicyModal(true)
  }

  const handleEditPolicyModalHide = () => {
    setShowEditPolicyModal(false)
    setSelectedRetentionPolicyId(null)
    navigate(`/workspace/setup/data-retention/`, { replace: true })
  }

  const handleViewPolicyModalHide = () => {
    setShowViewPolicyModal(false)
    setSelectedRetentionPolicyId(null)
    navigate(`/workspace/setup/data-retention`, { replace: true })
  }

  const handleDeletePolicyModalHide = () => {
    setShowDeletePolicyModal(false)
    setSelectedRetentionPolicyId(null)
  }

  const ruleConfigTemplate = (rowData: RetentionPolicy) => {
    const { rule } = rowData
    if (rule.type === RetentionRuleType.TTL) {
      const { config } = rule as TTLRetentionRule
      return <RetentionPolicyTTLConfigView ttlConfig={config} />
    }

    if (rule.type === RetentionRuleType.EVENT) {
      const ttlRetentionRule = rule as EventRetentionRule
      return <div>{ttlRetentionRule.config.name}</div>
    }
  }

  const resourceConfigTemplate = (rowData: RetentionPolicy) => {
    const { resource } = rowData
    if (resource.type === RetentionResourceType.ROOM) {
      return <div>{_ALL_RESOURCE_MESSAGE}</div>
    }
    if (resource.type === RetentionResourceType.SESSION) {
      const { config } = resource as SessionRetention
      return (
        <>
          {config ? (
            <div className='d-flex flex-column gap-2'>
              <div>kind: {getFileKindLabel(config.kind) || _ALL_RESOURCE_MESSAGE}</div>
              <div>source: {config.source || _ALL_RESOURCE_MESSAGE}</div>
            </div>
          ) : (
            _ALL_RESOURCE_MESSAGE
          )}
        </>
      )
    }
    if (resource.type === RetentionResourceType.FILE) {
      const { config } = resource as FileRetention
      return <div>{getFileKindLabel(config?.kind) || _ALL_RESOURCE_MESSAGE}</div>
    }
  }

  const columns: TableColumn<RetentionPolicy>[] = [
    {
      name: 'Id',
      selector: (row) => row.id,
      sortable: true,
    },
    {
      name: 'Rule Type',
      selector: (row) => row.rule.type,
      sortable: true,
      cell: (row) => <RetentionPolicyRuleType type={row.rule.type} />,
    },
    {
      name: 'Rule Config',
      selector: (row) => row.rule.type,
      sortable: true,
      cell: ruleConfigTemplate,
    },
    {
      name: 'Resource',
      selector: (row) => row.resource.type,
      cell: (row) => <RetentionPolicyResourceType type={row.resource.type} />,
      sortable: true,
    },
    {
      name: 'Resource Config',
      selector: (row) => row.rule.type,
      sortable: true,
      cell: resourceConfigTemplate,
    },

    {
      ignoreRowClick: true,
      right: true,
      button: true,
      allowOverflow: true,
      width: '3rem',
      cell: (row) => (
        <ActionButton>
          <ScopedBlock
            scope={{
              scope: 'retention_policy:read',
              superScope: ['retention_policy:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickViewPolicy(row)}>
              Show
            </Dropdown.Item>
          </ScopedBlock>
          <ScopedBlock
            scope={{
              scope: 'retention_policy:update',
              superScope: ['retention_policy:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
            authActions={[() => !configuredAtTheEnterpriseLevel]}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickEditPolicy(row)}>
              Edit
            </Dropdown.Item>
          </ScopedBlock>
          <ScopedBlock
            scope={{
              scope: 'retention_policy:delete',
              superScope: ['retention_policy:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
            authActions={[() => !configuredAtTheEnterpriseLevel]}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickDeletePolicy(row)}>
              Delete
            </Dropdown.Item>
          </ScopedBlock>
        </ActionButton>
      ),
    },
  ]

  return (
    <Container fluid className='p-0'>
      {selectedRetentionPolicyId ? (
        <CreateOrEditPolicyModal policyId={selectedRetentionPolicyId} show={showEditPolicyModal} onHide={handleEditPolicyModalHide} />
      ) : null}
      {selectedRetentionPolicyId ? (
        <ViewPolicyModal policyId={selectedRetentionPolicyId} show={showViewPolicyModal} onHide={handleViewPolicyModalHide} />
      ) : null}
      {selectedRetentionPolicyId ? (
        <DeletePolicyModal
          policy={data?.data.find((policy) => policy.id === selectedRetentionPolicyId)}
          show={showDeletePolicyModal}
          onHide={handleDeletePolicyModalHide}
        />
      ) : null}
      <Row>
        <Col>
          <ZocksDataTable
            columns={columns}
            data={data?.data.filter((policy) => !policy.isDefault) ?? []}
            progressPending={isFetching}
            noDataComponent={<div className='d-flex w-100 justify-content-start justify-content-md-center'>There are no custom policies.</div>}
          />
        </Col>
      </Row>
    </Container>
  )
}
